import {useCallback, useEffect, useRef, useState} from "react";
import {css, cx} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {rpAppLink} from "@pg-mono/rp-routes";

import {LeadModal} from "../../application/components/LeadModal";
import {ForYouModal} from "../../for_you/components/ForYouModal";
import {HomematchTinderModal} from "../../homematch_tinder/components/HomematchTinderModal";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {PersonalizedHomepageConsultantCard} from "../components/PersonalizedHomepage/PersonalizedHomepageConsultantCard";
import {PersonalizedHomepageHero} from "../components/PersonalizedHomepage/PersonalizedHomepageHero";
import {PersonalizedHomepageMissionCard} from "../components/PersonalizedHomepage/PersonalizedHomepageMissionCard";
import {PersonalizedHomepagePicksCard} from "../components/PersonalizedHomepage/PersonalizedHomepagePicksCard";
import {PersonalizedHomepagePricesCard} from "../components/PersonalizedHomepage/PersonalizedHomepagePricesCard";
import {PersonalizedHomepageRecentlyViewedCard} from "../components/PersonalizedHomepage/PersonalizedHomepageRecentlyViewedCard";
import {SchemaHomepage} from "../components/SchemaHomepage";
import {PERSONALIZED_HOMEPAGE_MISSION_CHANNEL_HREF, PERSONALIZED_HOMEPAGE_MISSION_VIDEO_HREF} from "../constants/personalized_homepage_mocks";
import {usePersonalizedHeroBackground} from "../hooks/use_personalized_hero_background";
import {usePersonalizedHomepageData} from "../hooks/use_personalized_homepage_data";
import {usePersonalizedHomepageImpression} from "../hooks/use_personalized_homepage_impression";
import {
    trackPersonalizedHomepageConsultantCall,
    trackPersonalizedHomepageOffersForYouClick,
    trackPersonalizedHomepagePricesShowMore,
    trackPersonalizedHomepageRecentlyViewedList,
    trackPersonalizedHomepageRecentlyViewedSelect,
    trackPersonalizedHomepageRecentSearchClick,
    trackPersonalizedHomepageRecentSearchesView,
    trackPersonalizedHomepageViewPage,
    trackPersonalizedHomepageYouTubeChannelClick,
    trackPersonalizedHomepageYouTubeVideoClick
} from "../tracking/track_personalized_homepage";

/** Alternative `/` composition rendered only by HomepageView after auth is resolved. */
export const AuthenticatedHomepageView = () => {
    const {data: userProfile} = useGetUserProfileQuery();
    const data = usePersonalizedHomepageData();
    const heroBackgroundImages = usePersonalizedHeroBackground();
    const hasTrackedViewPageRef = useRef(false);
    const [isTinderDeckOpen, setIsTinderDeckOpen] = useState(false);

    const recentSearches = data.recentSearches;
    const recentlyViewedPropertyDetails = data.recentlyViewedPropertyDetails;

    useEffect(() => {
        if (hasTrackedViewPageRef.current) {
            return;
        }

        hasTrackedViewPageRef.current = true;
        trackPersonalizedHomepageViewPage();
    }, []);

    const trackRecentSearchesImpression = useCallback(() => {
        if (recentSearches.length === 0) {
            return;
        }

        trackPersonalizedHomepageRecentSearchesView({searches: recentSearches});
    }, [recentSearches]);

    const trackRecentlyViewedImpression = useCallback(() => {
        if (recentlyViewedPropertyDetails.length === 0) {
            return;
        }

        trackPersonalizedHomepageRecentlyViewedList({properties: recentlyViewedPropertyDetails});
    }, [recentlyViewedPropertyDetails]);

    const recentSearchesSectionRef = usePersonalizedHomepageImpression<HTMLElement>({
        isEnabled: !data.recentSearchesState.isLoading && recentSearches.length > 0,
        onImpression: trackRecentSearchesImpression
    });
    const recentlyViewedSectionRef = usePersonalizedHomepageImpression<HTMLElement>({
        isEnabled: !data.recentlyViewedPropertiesState.isLoading && recentlyViewedPropertyDetails.length > 0,
        onImpression: trackRecentlyViewedImpression
    });

    return (
        <div className={authenticatedHomepageStyle}>
            <div>
                <PersonalizedHomepageHero
                    userName={userProfile?.name}
                    backgroundImages={heroBackgroundImages}
                    recentSearches={recentSearches}
                    areRecentSearchesLoading={data.recentSearchesState.isLoading}
                    recentSearchesSectionRef={recentSearchesSectionRef}
                    onRecentSearchClick={(search, index) => trackPersonalizedHomepageRecentSearchClick({search, index})}
                />

                <div className={personalizedContentStyle}>
                    <Container>
                        <div className={bentoStyle}>
                            <div className={bentoLeftStyle}>
                                <div className={bentoLeftTopStyle}>
                                    <div className={cx(bentoCellStyle, bentoPicksCellStyle)}>
                                        <PersonalizedHomepagePicksCard
                                            teaser={data.recommendationsTeaser}
                                            onRecommendationsClick={(ctaLabel) => trackPersonalizedHomepageOffersForYouClick({label: ctaLabel})}
                                            onOpenRecommendations={data.hasHomematchRecommendations ? () => setIsTinderDeckOpen(true) : undefined}
                                        />
                                    </div>

                                    <div className={bentoCellStyle}>
                                        <PersonalizedHomepagePricesCard
                                            prices={data.locationPrices}
                                            latestPricedMonth={data.locationPricesLatestMonth}
                                            isLoading={data.locationPricesState.isLoading}
                                            hasError={data.locationPricesState.isError && data.locationPrices.length === 0}
                                            onShowMoreClick={(ctaLabel) => trackPersonalizedHomepagePricesShowMore({label: ctaLabel})}
                                        />
                                    </div>
                                </div>

                                <div className={bentoCellStyle}>
                                    <PersonalizedHomepageRecentlyViewedCard
                                        properties={data.recentlyViewedProperties}
                                        isLoading={data.recentlyViewedPropertiesState.isLoading}
                                        hasError={data.recentlyViewedPropertiesState.isError}
                                        emptyStateHref={rpAppLink.offer.list()}
                                        sectionRef={recentlyViewedSectionRef}
                                        onPropertyClick={(_property, index) => {
                                            const propertyDetail = recentlyViewedPropertyDetails[index];

                                            if (propertyDetail) {
                                                trackPersonalizedHomepageRecentlyViewedSelect({property: propertyDetail, index});
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            <div className={bentoRightStyle}>
                                <div className={bentoCellStyle}>
                                    <PersonalizedHomepageConsultantCard
                                        onConsultantClick={({ctaLabel, consultantName, phoneNumber}) =>
                                            trackPersonalizedHomepageConsultantCall({label: ctaLabel, consultantName, phoneNumber})
                                        }
                                    />
                                </div>

                                <div className={bentoCellStyle}>
                                    <PersonalizedHomepageMissionCard
                                        videoHref={PERSONALIZED_HOMEPAGE_MISSION_VIDEO_HREF}
                                        channelHref={PERSONALIZED_HOMEPAGE_MISSION_CHANNEL_HREF}
                                        onVideoClick={(videoHref) => trackPersonalizedHomepageYouTubeVideoClick({videoUrl: videoHref})}
                                        onChannelClick={(ctaLabel) =>
                                            trackPersonalizedHomepageYouTubeChannelClick({
                                                label: ctaLabel,
                                                channelUrl: PERSONALIZED_HOMEPAGE_MISSION_CHANNEL_HREF
                                            })
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </Container>
                </div>
            </div>

            <HomematchTinderModal isOpen={isTinderDeckOpen} onClose={() => setIsTinderDeckOpen(false)} />
            <ForYouModal />
            <LeadModal />
            <SchemaHomepage />
        </div>
    );
};

const authenticatedHomepageStyle = css({
    minWidth: 0
});

const personalizedContentStyle = css({
    paddingTop: 2,
    paddingBottom: 4,
    backgroundColor: "var(--colors-background-100)",

    md: {
        paddingTop: 3,
        paddingBottom: 8
    }
});

/**
 * Figma mobile: single column. From md the bento splits into a fluid left
 * column and a fixed 415px right rail (1440 ref: 693/415, 1920 ref: 1077/415).
 * Side inset comes from Container.
 */
const bentoStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,

    sm: {
        gap: 2.5
    },

    md: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 41.5rem",
        gap: 2.5,
        alignItems: "start"
    }
});

const bentoLeftStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    minWidth: 0
});

/**
 * The picks | prices split next to each other exists from the lg breakpoint on
 * Figma frame. The prices cell stretches to the row (footer pinned to the card
 * bottom), while the picks cell keeps its natural height so an expanded price
 * list does not inflate it — matching both the collapsed and expanded frames.
 */
const bentoLeftTopStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2.5,

    lg: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 388fr) minmax(0, 668fr)"
    }
});

/** Floats next to the prices card when "Pokaż więcej" makes it taller than the viewport. */
const bentoPicksCellStyle = css({
    lg: {
        position: "sticky",
        top: "8.4rem",
        alignSelf: "start"
    }
});

/**
 * From md the right rail floats while the taller left column scrolls. The top
 * offset clears the headroom navigation (68px) when it pins back on scroll-up.
 */
const bentoRightStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    minWidth: 0,

    md: {
        position: "sticky",
        top: "8.4rem",
        alignSelf: "start"
    }
});

const bentoCellStyle = css({
    display: "flex",
    flexDirection: "column",
    minWidth: 0
});
import {useCallback, useEffect, useRef, useState} from "react";
import {css, cx} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {rpAppLink} from "@pg-mono/rp-routes";

import {LeadModal} from "../../application/components/LeadModal";
import {ForYouModal} from "../../for_you/components/ForYouModal";
import {HomematchTinderModal} from "../../homematch_tinder/components/HomematchTinderModal";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {PersonalizedHomepageConsultantCard} from "../components/PersonalizedHomepage/PersonalizedHomepageConsultantCard";
import {PersonalizedHomepageHero} from "../components/PersonalizedHomepage/PersonalizedHomepageHero";
import {PersonalizedHomepageMissionCard} from "../components/PersonalizedHomepage/PersonalizedHomepageMissionCard";
import {PersonalizedHomepagePicksCard} from "../components/PersonalizedHomepage/PersonalizedHomepagePicksCard";
import {PersonalizedHomepagePricesCard} from "../components/PersonalizedHomepage/PersonalizedHomepagePricesCard";
import {PersonalizedHomepageRecentlyViewedCard} from "../components/PersonalizedHomepage/PersonalizedHomepageRecentlyViewedCard";
import {SchemaHomepage} from "../components/SchemaHomepage";
import {PERSONALIZED_HOMEPAGE_MISSION_CHANNEL_HREF, PERSONALIZED_HOMEPAGE_MISSION_VIDEO_HREF} from "../constants/personalized_homepage_mocks";
import {usePersonalizedHeroBackground} from "../hooks/use_personalized_hero_background";
import {usePersonalizedHomepageData} from "../hooks/use_personalized_homepage_data";
import {usePersonalizedHomepageImpression} from "../hooks/use_personalized_homepage_impression";
import {
    trackPersonalizedHomepageConsultantCall,
    trackPersonalizedHomepageOffersForYouClick,
    trackPersonalizedHomepagePricesShowMore,
    trackPersonalizedHomepageRecentlyViewedList,
    trackPersonalizedHomepageRecentlyViewedSelect,
    trackPersonalizedHomepageRecentSearchClick,
    trackPersonalizedHomepageRecentSearchesView,
    trackPersonalizedHomepageViewPage,
    trackPersonalizedHomepageYouTubeChannelClick,
    trackPersonalizedHomepageYouTubeVideoClick
} from "../tracking/track_personalized_homepage";

/** Alternative `/` composition rendered only by HomepageView after auth is resolved. */
export const AuthenticatedHomepageView = () => {
    const {data: userProfile} = useGetUserProfileQuery();
    const data = usePersonalizedHomepageData();
    const heroBackgroundImages = usePersonalizedHeroBackground();
    const hasTrackedViewPageRef = useRef(false);
    const [isTinderDeckOpen, setIsTinderDeckOpen] = useState(false);

    const recentSearches = data.recentSearches;
    const recentlyViewedPropertyDetails = data.recentlyViewedPropertyDetails;

    useEffect(() => {
        if (hasTrackedViewPageRef.current) {
            return;
        }

        hasTrackedViewPageRef.current = true;
        trackPersonalizedHomepageViewPage();
    }, []);

    const trackRecentSearchesImpression = useCallback(() => {
        if (recentSearches.length === 0) {
            return;
        }

        trackPersonalizedHomepageRecentSearchesView({searches: recentSearches});
    }, [recentSearches]);

    const trackRecentlyViewedImpression = useCallback(() => {
        if (recentlyViewedPropertyDetails.length === 0) {
            return;
        }

        trackPersonalizedHomepageRecentlyViewedList({properties: recentlyViewedPropertyDetails});
    }, [recentlyViewedPropertyDetails]);

    const recentSearchesSectionRef = usePersonalizedHomepageImpression<HTMLElement>({
        isEnabled: !data.recentSearchesState.isLoading && recentSearches.length > 0,
        onImpression: trackRecentSearchesImpression
    });
    const recentlyViewedSectionRef = usePersonalizedHomepageImpression<HTMLElement>({
        isEnabled: !data.recentlyViewedPropertiesState.isLoading && recentlyViewedPropertyDetails.length > 0,
        onImpression: trackRecentlyViewedImpression
    });

    return (
        <div className={authenticatedHomepageStyle}>
            <div>
                <PersonalizedHomepageHero
                    userName={userProfile?.name}
                    backgroundImages={heroBackgroundImages}
                    recentSearches={recentSearches}
                    areRecentSearchesLoading={data.recentSearchesState.isLoading}
                    recentSearchesSectionRef={recentSearchesSectionRef}
                    onRecentSearchClick={(search, index) => trackPersonalizedHomepageRecentSearchClick({search, index})}
                />

                <div className={personalizedContentStyle}>
                    <Container>
                        <div className={bentoStyle}>
                            <div className={bentoLeftStyle}>
                                <div className={bentoLeftTopStyle}>
                                    <div className={cx(bentoCellStyle, bentoPicksCellStyle)}>
                                        <PersonalizedHomepagePicksCard
                                            teaser={data.recommendationsTeaser}
                                            onRecommendationsClick={(ctaLabel) => trackPersonalizedHomepageOffersForYouClick({label: ctaLabel})}
                                            onOpenRecommendations={data.hasHomematchRecommendations ? () => setIsTinderDeckOpen(true) : undefined}
                                        />
                                    </div>

                                    <div className={bentoCellStyle}>
                                        <PersonalizedHomepagePricesCard
                                            prices={data.locationPrices}
                                            latestPricedMonth={data.locationPricesLatestMonth}
                                            isLoading={data.locationPricesState.isLoading}
                                            hasError={data.locationPricesState.isError && data.locationPrices.length === 0}
                                            onShowMoreClick={(ctaLabel) => trackPersonalizedHomepagePricesShowMore({label: ctaLabel})}
                                        />
                                    </div>
                                </div>

                                <div className={bentoCellStyle}>
                                    <PersonalizedHomepageRecentlyViewedCard
                                        properties={data.recentlyViewedProperties}
                                        isLoading={data.recentlyViewedPropertiesState.isLoading}
                                        hasError={data.recentlyViewedPropertiesState.isError}
                                        emptyStateHref={rpAppLink.offer.list()}
                                        sectionRef={recentlyViewedSectionRef}
                                        onPropertyClick={(_property, index) => {
                                            const propertyDetail = recentlyViewedPropertyDetails[index];

                                            if (propertyDetail) {
                                                trackPersonalizedHomepageRecentlyViewedSelect({property: propertyDetail, index});
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            <div className={bentoRightStyle}>
                                <div className={bentoCellStyle}>
                                    <PersonalizedHomepageConsultantCard
                                        onConsultantClick={({ctaLabel, consultantName, phoneNumber}) =>
                                            trackPersonalizedHomepageConsultantCall({label: ctaLabel, consultantName, phoneNumber})
                                        }
                                    />
                                </div>

                                <div className={bentoCellStyle}>
                                    <PersonalizedHomepageMissionCard
                                        videoHref={PERSONALIZED_HOMEPAGE_MISSION_VIDEO_HREF}
                                        channelHref={PERSONALIZED_HOMEPAGE_MISSION_CHANNEL_HREF}
                                        onVideoClick={(videoHref) => trackPersonalizedHomepageYouTubeVideoClick({videoUrl: videoHref})}
                                        onChannelClick={(ctaLabel) =>
                                            trackPersonalizedHomepageYouTubeChannelClick({
                                                label: ctaLabel,
                                                channelUrl: PERSONALIZED_HOMEPAGE_MISSION_CHANNEL_HREF
                                            })
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </Container>
                </div>
            </div>

            <HomematchTinderModal isOpen={isTinderDeckOpen} onClose={() => setIsTinderDeckOpen(false)} />
            <ForYouModal />
            <LeadModal />
            <SchemaHomepage />
        </div>
    );
};

const authenticatedHomepageStyle = css({
    minWidth: 0
});

const personalizedContentStyle = css({
    paddingTop: 2,
    paddingBottom: 4,
    backgroundColor: "var(--colors-background-100)",

    md: {
        paddingTop: 3,
        paddingBottom: 8
    }
});

/**
 * Figma mobile: single column. From md the bento splits into a fluid left
 * column and a fixed 415px right rail (1440 ref: 693/415, 1920 ref: 1077/415).
 * Side inset comes from Container.
 */
const bentoStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,

    sm: {
        gap: 2.5
    },

    md: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 41.5rem",
        gap: 2.5,
        alignItems: "start"
    }
});

const bentoLeftStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    minWidth: 0
});

/**
 * The picks | prices split next to each other exists from the lg breakpoint on
 * Figma frame. The prices cell stretches to the row (footer pinned to the card
 * bottom), while the picks cell keeps its natural height so an expanded price
 * list does not inflate it — matching both the collapsed and expanded frames.
 */
const bentoLeftTopStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2.5,

    lg: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 388fr) minmax(0, 668fr)"
    }
});

/** Floats next to the prices card when "Pokaż więcej" makes it taller than the viewport. */
const bentoPicksCellStyle = css({
    lg: {
        position: "sticky",
        top: "8.4rem",
        alignSelf: "start"
    }
});

/**
 * From md the right rail floats while the taller left column scrolls. The top
 * offset clears the headroom navigation (68px) when it pins back on scroll-up.
 */
const bentoRightStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    minWidth: 0,

    md: {
        position: "sticky",
        top: "8.4rem",
        alignSelf: "start"
    }
});

const bentoCellStyle = css({
    display: "flex",
    flexDirection: "column",
    minWidth: 0
});
